import {
    Box,
    Button,
    Modal,
    Select,
    SelectProps,
    SpaceBetween,
} from '@cloudscape-design/components';
import { useState, useEffect } from 'react';
import { usePreferences } from './providers/PreferencesProvider';
import { useTranslate } from 'src/i18n/useTranslate';
import { PREFERENCE_DISPLAYED_LANGUAGES } from 'src/constants';

interface PreferencesPaneProps {
    preferencesOpen: boolean;
}

export default function PreferencesPane({
    preferencesOpen,
}: PreferencesPaneProps) {
    const t = useTranslate();
    const [selectedLanguage, setSelectedLanguage] =
        useState<SelectProps.Option | null>(null);

    const {
        selectedUserPreferences,
        languages,
        isSaving,
        handleSelectionChange,
        resetPreferencesSelection,
        savePreferences,
    } = usePreferences();

    const languageOptions: SelectProps.Option[] = languages.map((language) => ({
        value: String(language.id),
        label: PREFERENCE_DISPLAYED_LANGUAGES[language.id] || language.name,
    }));

    useEffect(() => {
        if (selectedUserPreferences.language) {
            const option = languageOptions.find(
                (opt) =>
                    opt.value === String(selectedUserPreferences.language.id)
            );
            setSelectedLanguage(option || null);
        }
    }, [selectedUserPreferences.language, preferencesOpen]);

    const handleSave = () => {
        savePreferences();
    };

    const handleCancel = () => {
        resetPreferencesSelection();
    };

    return (
        <Modal
            onDismiss={handleCancel}
            visible={preferencesOpen}
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button
                            variant="link"
                            disabled={isSaving}
                            onClick={handleCancel}
                        >
                            {t('cancel_button', 'Cancel')}
                        </Button>
                        <Button
                            variant="primary"
                            loading={isSaving}
                            data-testid="save-button"
                            onClick={handleSave}
                        >
                            {t(
                                isSaving
                                    ? 'user_preference_saving_preferences_loading_message'
                                    : 'save_button',
                                isSaving ? 'Saving User Preferences' : 'Save'
                            )}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
            header={t('user_preference_select_langauage', 'Language')}
        >
            <Select
                selectedOption={selectedLanguage}
                onChange={({ detail }) => {
                    setSelectedLanguage(detail.selectedOption);
                    handleSelectionChange(
                        'language',
                        detail.selectedOption.value || ''
                    );
                }}
                options={languageOptions}
                disabled={isSaving}
                data-testid="language-select"
            />
        </Modal>
    );
}
